
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { initSentry } from './lib/sentry'
import { initPostHog } from './lib/posthog'
import App from './App.tsx'
import './index.css'

// Defer 3rd-party init until after first paint so it doesn't push out LCP/FCP.
// `requestIdleCallback` fires when the browser is idle; falls back to setTimeout.
const onIdle: (cb: () => void) => void =
  (globalThis as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
    ? (cb) => (globalThis as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(cb)
    : (cb) => setTimeout(cb, 800);

onIdle(() => {
  initSentry();
  initPostHog();
});

const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
    <div className="max-w-md">
      <h1 className="text-3xl font-bold text-red-600 mb-4">Something went wrong</h1>
      <p className="text-gray-700 mb-4">Sorry, an unexpected error has occurred.</p>
      <pre className="bg-gray-100 p-4 rounded-md text-left text-sm overflow-auto max-w-full mb-4">
        {error.message}
      </pre>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
      >
        Try again
      </button>
    </div>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
    <ErrorFallback error={error as Error} resetErrorBoundary={resetError} />
  )}>
    <App />
  </Sentry.ErrorBoundary>
);
